import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthContext';
import { login as loginApi, logout as logoutApi, fetchCurrentUser, verifyTwoFactor, resendTwoFactor, } from '@/features/shared/services/authService';
import { useQueryClient } from 'react-query';
import { onUnauthorized } from '@/features/shared/services/authBus';
export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pending2FAEmail, setPending2FAEmail] = useState(null);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const handling401Ref = useRef(false);
    useEffect(() => {
        let isMounted = true;
        fetchCurrentUser()
            .then((u) => isMounted && setUser(u))
            .catch(() => isMounted && setUser(null))
            .finally(() => isMounted && setLoading(false));
        return () => {
            isMounted = false;
        };
    }, []);
    const logout = useCallback(async () => {
        try {
            if (user) {
                await logoutApi();
            }
        }
        catch {
            // ignore error
        }
        setPending2FAEmail(null);
        setUser(null);
        await queryClient.cancelQueries({ type: 'active' });
        queryClient.clear();
        // Avoid navigating if already on a public route
        const publicPaths = ['/login', '/forgot-password', '/reset-password', '/reset', '/verify-2fa', '/activate-account'];
        const currentPath = window.location.pathname || '';
        const isAlreadyPublic = publicPaths.some((p) => currentPath.startsWith(p));
        if (!isAlreadyPublic) {
            navigate('/login', { replace: true });
        }
    }, [navigate, queryClient, user]);
    useEffect(() => {
        const unsubscribe = onUnauthorized(async () => {
            // Only trigger logout on 401 if a user is currently authenticated
            if (handling401Ref.current || !user)
                return;
            handling401Ref.current = true;
            try {
                await logout();
            }
            finally {
                handling401Ref.current = false;
            }
        });
        return unsubscribe;
    }, [logout, user]);
    const login = async (email, password) => {
        const res = await loginApi({ email, password });
        if (res.status === 'NEED_2FA') {
            setPending2FAEmail(email);
            return 'NEED_2FA';
        }
        const currentUser = await fetchCurrentUser();
        setUser(currentUser);
        queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === 'tasks' ||
                query.queryKey[0] === 'clients' ||
                query.queryKey[0] === 'projects',
        });
        return 'SUCCESS';
    };
    const verifyOtp = async (otp) => {
        if (!pending2FAEmail)
            return;
        await verifyTwoFactor({ email: pending2FAEmail, otp });
        const currentUser = await fetchCurrentUser();
        setUser(currentUser);
        setPending2FAEmail(null);
        queryClient.invalidateQueries({
            predicate: (query) => query.queryKey[0] === 'tasks' ||
                query.queryKey[0] === 'clients' ||
                query.queryKey[0] === 'projects',
        });
    };
    const resendOtp = async () => {
        if (!pending2FAEmail)
            return;
        await resendTwoFactor(pending2FAEmail);
    };
    const refreshUser = async () => {
        const currentUser = await fetchCurrentUser();
        setUser(currentUser);
    };
    return (_jsx(AuthContext.Provider, { value: { user, loading, pending2FAEmail, login, verifyOtp, resendOtp, logout, refreshUser }, children: children }));
};
